import React from 'react';
import { Container, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import InstagramIcon from '@mui/icons-material/Instagram';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import EmailIcon from '@mui/icons-material/Email';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';

import { useSocialChannels, useAboutData } from '../hooks/useContent';

const renderIcon = (type: string, color: string) => {
  switch (type) {
    case 'instagram':
      return <InstagramIcon sx={{ fontSize: 24, color }} />;
    case 'github':
      return <GitHubIcon sx={{ fontSize: 24, color }} />;
    case 'linkedin':
      return <LinkedInIcon sx={{ fontSize: 24, color }} />;
    case 'twitter':
      return <TwitterIcon sx={{ fontSize: 24, color }} />;
    case 'email':
      return <EmailIcon sx={{ fontSize: 24, color }} />;
    default:
      return <ArrowOutwardIcon sx={{ fontSize: 24, color }} />;
  }
};

const SocialLinks: React.FC = () => {
  const { channels } = useSocialChannels();
  const { about } = useAboutData();
  return (
    <main
      style={{
        position: 'relative',
        zIndex: 1,
        minHeight: 'calc(100vh - 64px)',
        paddingTop: 48,
        paddingBottom: 80,
      }}
    >
      <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
        {/* Header block */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{ textAlign: 'center', marginBottom: 40 }}
        >
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: '22px',
              background: 'linear-gradient(135deg, #EFC430 0%, #C99A16 100%)',
              color: '#0A0A0A',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 800,
              fontSize: '1.8rem',
              margin: '0 auto 16px auto',
              boxShadow: '0 8px 24px rgba(239, 196, 48, 0.25)',
            }}
          >
            {about.avatarText}
          </div>

          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.8rem', sm: '2.4rem' },
              letterSpacing: '-0.02em',
              background: 'linear-gradient(135deg, #EFC430 0%, #F4D675 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
            }}
          >
            {about.handle}
          </Typography>

          <Typography
            variant="body1"
            sx={{ color: '#9CA3AF', maxWidth: 540, mx: 'auto', lineHeight: 1.6 }}
          >
            A personal hub connecting the different facets of life — personal life & friends on Instagram, code on GitHub, professional network on LinkedIn, and direct contact.
          </Typography>
        </motion.div>

        {/* Life Graph Channels List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {channels.map((channel) => (
            <a
              key={channel.id}
              href={channel.url}
              target={channel.url.startsWith('http') ? '_blank' : undefined}
              rel="noopener noreferrer"
              style={{
                textDecoration: 'none',
                borderRadius: 16,
                backgroundColor: 'rgba(20, 20, 22, 0.65)',
                backdropFilter: 'blur(16px)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                padding: '20px 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'border-color 0.25s ease, transform 0.25s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div
                  style={{
                    padding: 10,
                    borderRadius: 12,
                    backgroundColor: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {renderIcon(channel.icon, channel.accent)}
                </div>

                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Typography
                      variant="h6"
                      sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.2 }}
                    >
                      {channel.platform}
                    </Typography>

                    <span
                      style={{
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        color: '#9CA3AF',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        padding: '2px 8px',
                        borderRadius: 6,
                      }}
                    >
                      {channel.context}
                    </span>
                  </div>

                  <Typography
                    variant="body2"
                    sx={{ color: '#9CA3AF', fontSize: '0.84rem', mt: 0.5 }}
                  >
                    {channel.description}
                  </Typography>
                </div>
              </div>

              <span style={{ color: channel.accent, fontSize: '1.1rem', paddingLeft: 12 }}>↗</span>
            </a>
          ))}
        </div>
      </Container>
    </main>
  );
};

export default SocialLinks;
